import { useCallback, useEffect, useState } from 'react';

import { API_BASE_PATH } from '../apiConfig';
import RunIssueDashboard from './RunIssueDashboard';
import RunStatusPage from './RunStatusPage';

type RunPageLoadState = 'loading' | 'refreshing' | 'ready' | 'error';

interface RunPageProps {
  runId: string;
}

interface RunStatusModel {
  model: string;
  aiAgent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  command?: string;
  exitCode?: number;
  timedOut?: boolean;
  issueCount?: number;
  submissionErrorCount?: number;
  error?: string;
}

interface RunLogEntry {
  time: string;
  message: string;
}

interface RunStatusAgent {
  agent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  logFilePath?: string;
  logs?: RunLogEntry[];
  models: RunStatusModel[];
  error?: string;
}

interface RunRequestedAiAgentModel {
  aiAgent: string;
  model?: string;
}

interface RunStatusDeduplication {
  status: string;
  finished: boolean;
  model?: string;
  judgeModel?: string;
  startedAt?: string;
  finishedAt?: string;
  outputPath?: string;
  issueCount?: number;
  deduplicatedIssueCount?: number;
  reportedIssueCount?: number;
  openAiRequestCount?: number;
  error?: string;
}

interface RunStatusSnapshot {
  runId: string;
  target: string;
  requestedSkills: string[];
  requestedAiAgents: string[];
  requestedAiAgentModels?: RunRequestedAiAgentModel[];
  runDirectory: string;
  statusFilePath: string;
  branch?: string;
  scopePath?: string;
  scopePaths?: string[];
  preconditionsPath?: string;
  clonedRepositoryPath?: string;
  repositoryPath?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  agents: RunStatusAgent[];
  deduplication?: RunStatusDeduplication;
  report?: RunStatusDeduplication;
  setupError?: string;
}

interface RunIssueSubmission {
  Title: string;
  Summary: string;
  Detail: string;
  Impact: string;
  Files: string[];
  severity?: string;
}

interface RunRawIssue {
  issueId: number;
  skill: string;
  scanAgent?: string;
  aiAgent?: string;
  relativePath: string;
  issue: RunIssueSubmission;
}

interface RunReportedIssue extends RunIssueSubmission {
  id: string;
  sourceIssueIds: number[];
  sources: RunRawIssue[];
  duplicateReasons: string[];
}

interface RunIssueReadError {
  relativePath: string;
  error: string;
}

type RunIssueAcceptanceStatus = 'acknowledged' | 'accepted';

interface RunAcceptedIssue {
  id: string;
  status: RunIssueAcceptanceStatus;
  updatedAt: string;
}

interface RunIssuesPayload {
  allIssues?: RunRawIssue[];
  duplicatedIssues?: RunRawIssue[];
  deduplicatedIssues?: RunReportedIssue[];
  reportedIssues?: RunReportedIssue[];
  acceptedIssues?: RunAcceptedIssue[];
  readErrors?: RunIssueReadError[];
  deduplicationGeneratedAt?: string;
  reportGeneratedAt?: string;
  acceptedIssuesUpdatedAt?: string;
}

interface RunDetailsResponse {
  status: RunStatusSnapshot;
  issues: RunIssuesPayload;
}

interface RunIssueViewModel {
  id: string;
  title: string;
  summary: string;
  detail: string;
  impact: string;
  files: string[];
  sources: string[];
  severity?: string;
  acceptanceStatus?: RunIssueAcceptanceStatus;
  acceptanceUpdatedAt?: string;
}

interface RunAcceptedIssueUpdateResponse {
  acceptedIssues: RunAcceptedIssue[];
  acceptedIssuesUpdatedAt?: string;
}

const RUN_POLL_INTERVAL_MS = 3000;
const RUN_TERMINAL_STATUSES = ['completed', 'partial', 'failed'];
const RUN_REPORT_TERMINAL_STATUSES = ['completed', 'failed', 'skipped'];

const runFormatErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };

    if (typeof error === 'string' && error.trim()) {
      return error;
    }
  }

  return `Request failed with status ${fallbackStatus}`;
};

const runRequestJson = async <ResponseType,>(path: string, init: RequestInit = {}): Promise<ResponseType> => {
  const headers = new Headers(init.headers);

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    headers
  });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (!response.ok) {
    throw new Error(runFormatErrorMessage(payload, response.status));
  }

  return payload as ResponseType;
};

const runGetIssueMarkdownExportUrl = (runId: string): string => {
  return `${API_BASE_PATH}/runs/${encodeURIComponent(runId)}/issues.md`;
};

const runGetIssuePdfExportUrl = (runId: string): string => {
  return `${API_BASE_PATH}/runs/${encodeURIComponent(runId)}/issues.pdf`;
};

const runGetResultProcessingStatus = (status: RunStatusSnapshot | undefined): RunStatusDeduplication | undefined => {
  return status?.deduplication ?? status?.report;
};

const runIsReady = (details: RunDetailsResponse | null): boolean => {
  if (!details) {
    return false;
  }

  const resultProcessing = runGetResultProcessingStatus(details.status);

  return RUN_TERMINAL_STATUSES.includes(details.status.status)
    && Boolean(resultProcessing)
    && RUN_REPORT_TERMINAL_STATUSES.includes(resultProcessing?.status ?? '');
};

const runGetIssues = (details: RunDetailsResponse | null): RunIssueViewModel[] => {
  if (!details?.issues) {
    return [];
  }

  const acceptedById = new Map(
    (details.issues.acceptedIssues ?? []).map((issue) => [issue.id, issue])
  );
  const reportedIssues = details.issues.deduplicatedIssues ?? details.issues.reportedIssues ?? [];

  if (reportedIssues.length > 0) {
    return reportedIssues.map((issue) => {
      const acceptedIssue = acceptedById.get(issue.id);

      return {
        id: issue.id,
        title: issue.Title,
        summary: issue.Summary,
        detail: issue.Detail,
        impact: issue.Impact,
        files: issue.Files,
        severity: issue.severity,
        acceptanceStatus: acceptedIssue?.status,
        acceptanceUpdatedAt: acceptedIssue?.updatedAt,
        sources: issue.sources.map((source) => {
          const scanAgent = source.scanAgent ? ` / ${source.scanAgent}` : '';
          const aiAgent = source.aiAgent ? ` / ${source.aiAgent}` : '';

          return `${source.skill}${scanAgent}${aiAgent} #${source.issueId}`;
        })
      };
    });
  }

  return (details.issues.allIssues ?? []).map((entry) => {
    const id = String(entry.issueId);
    const acceptedIssue = acceptedById.get(id);

    return {
      id,
      title: entry.issue.Title,
      summary: entry.issue.Summary,
      detail: entry.issue.Detail,
      impact: entry.issue.Impact,
      files: entry.issue.Files,
      severity: entry.issue.severity,
      acceptanceStatus: acceptedIssue?.status,
      acceptanceUpdatedAt: acceptedIssue?.updatedAt,
      sources: [`${entry.skill}${entry.scanAgent ? ` / ${entry.scanAgent}` : ''}${entry.aiAgent ? ` / ${entry.aiAgent}` : ''}`]
    };
  });
};

type RunPageScreen = 'status' | 'issues';

function RunPage({ runId }: RunPageProps) {
  const [loadState, setLoadState] = useState<RunPageLoadState>('loading');
  const [details, setDetails] = useState<RunDetailsResponse | null>(null);
  const [error, setError] = useState('');
  const [screen, setScreen] = useState<RunPageScreen>('status');
  const [nowMs, setNowMs] = useState(() => Date.now());

  const isReady = runIsReady(details);
  const issues = runGetIssues(details);

  const loadRun = useCallback(async () => {
    setLoadState((previous) => previous === 'ready' ? 'refreshing' : previous === 'error' ? 'loading' : previous);
    setError('');

    try {
      const response = await runRequestJson<RunDetailsResponse>(`/getResults/${encodeURIComponent(runId)}`);

      setDetails(response);
      setLoadState('ready');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
      setLoadState('error');
    }
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      setLoadState((previous) => previous === 'ready' ? 'refreshing' : previous === 'error' ? 'loading' : previous);
      setError('');

      try {
        const response = await runRequestJson<RunDetailsResponse>(`/getResults/${encodeURIComponent(runId)}`);

        if (cancelled) {
          return;
        }

        setDetails(response);
        setLoadState('ready');

        if (!runIsReady(response)) {
          timer = window.setTimeout(poll, RUN_POLL_INTERVAL_MS);
        }
      } catch (pollError) {
        if (cancelled) {
          return;
        }

        setError(pollError instanceof Error ? pollError.message : String(pollError));
        setLoadState('error');
      }
    };

    void poll();

    return () => {
      cancelled = true;

      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [runId]);

  useEffect(() => {
    setNowMs(Date.now());

    if (isReady) {
      return undefined;
    }

    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [isReady]);

  const handleIssueAcceptanceChange = useCallback(async (
    issueId: string,
    acceptanceStatus: RunIssueAcceptanceStatus | null
  ) => {
    const response = await runRequestJson<RunAcceptedIssueUpdateResponse>(
      `/runs/${encodeURIComponent(runId)}/issues/${encodeURIComponent(issueId)}/acceptance`,
      {
        method: 'POST',
        body: JSON.stringify({ status: acceptanceStatus ?? 'open' })
      }
    );

    setDetails((currentDetails) => currentDetails
      ? {
        ...currentDetails,
        issues: {
          ...currentDetails.issues,
          acceptedIssues: response.acceptedIssues,
          acceptedIssuesUpdatedAt: response.acceptedIssuesUpdatedAt
        }
      }
      : currentDetails);
  }, [runId]);

  const status = details?.status;

  return (
    <div className={`app-shell run-shell${screen === 'issues' ? ' issues-view' : ''}`}>
      <header className="top-bar">
        <a className="logo" href="/">
          <img className="logo-mark" src="/logoinvert.png" alt="" aria-hidden="true" />
          <span className="logo-text">Havu</span>
        </a>
        <div className="run-header-actions">
          {isReady && (
            <button
              type="button"
              className="secondary-link"
              onClick={() => setScreen((s) => s === 'issues' ? 'status' : 'issues')}
            >
              {screen === 'issues' ? 'View status' : 'View results'}
            </button>
          )}
          <a className="secondary-link" href="/">
            New scan
          </a>
        </div>
      </header>

      {screen === 'issues' ? (
        <RunIssueDashboard
          issues={issues}
          markdownExportUrl={runGetIssueMarkdownExportUrl(runId)}
          pdfExportUrl={runGetIssuePdfExportUrl(runId)}
          onIssueAcceptanceChange={handleIssueAcceptanceChange}
        />
      ) : (
        <RunStatusPage
          status={status}
          loadState={loadState}
          error={error}
          isReady={isReady}
          issueCount={issues.length}
          nowMs={nowMs}
          onRetry={() => void loadRun()}
          onViewResults={() => setScreen('issues')}
        />
      )}
    </div>
  );
}

export default RunPage;

export type {
  RunIssueAcceptanceStatus,
  RunIssueViewModel,
  RunPageLoadState,
  RunStatusAgent,
  RunStatusDeduplication,
  RunStatusSnapshot
};
